// React
import React, { useEffect } from 'react'
import PropTypes from 'prop-types'

// Libraries
import isEqual from 'lodash/isEqual'
import isEmpty from 'lodash/isEmpty'
import { useFormikContext } from 'formik'

// Providers
import { PickerContextProvider } from './Picker'

// Shared
import OperatingRulesUtils from 'shared/utils/operating_rules'
import {
  isDaySelectable as _isDaySelectable,
  isTimeSelectable as _isTimeSelectable,
  validate,
  getDefaultTime
} from 'ui/components/Picker/utils'
import objectDiff from 'shared/utils/objectDiff'
import withForm from 'shared/form'
import { useOrm } from 'shared/utils/useOrm'

const PickerFormComponent = ({
  children,
  mode,
  type,
  openingHours,
  allowPast,
  useTimes,
  minDate,
  onValidate,
  onChange,
  validate
}) => {
  const form = useFormikContext()

  const timeslots = useOrm('operating_rules', (rules) => {
    return rules.getAll({ data_type: 'timeslot_fixed' })
  })

  const hasInitialValues = !!form.initialValues.starts_at && !!form.initialValues.stops_at

  useEffect(() => {
    const errors = validate(form.values)
    const valid = isEmpty({ ...errors, ...form.errors })

    onValidate?.(valid, { ...errors, ...form.errors })
  }, [])

  useEffect(() => {
    const errors = validate(form.values)
    const valid = isEmpty({ ...errors, ...form.errors })

    onValidate?.(valid, { ...errors, ...form.errors })

    if (!isEqual(form.values, form.initialValues)) {
      if (valid) {
        const promise = onChange?.(form.values)

        promise?.catch?.(() => {
          const changedKeys = Object.keys(objectDiff(form.values, form.initialValues))
          const fieldErrors = {}

          changedKeys.forEach((key) => {
            if (['starts_at', 'stops_at'].includes(key)) {
              fieldErrors[key] = { date: 'invalid', time: 'invalid' }
            } else {
              fieldErrors[key] = 'is invalid'
            }
          })

          setTimeout(() => {
            form.setErrors(fieldErrors)
          }, 0)
        })
      }

      if (!valid && errors.stop_location_id) {
        // TODO: Understand if we need this
        form.setFieldValue('stop_location_id', form.values.start_location_id)

        setTimeout(() => {
          form.setFieldValue('stop_location_id', form.values.start_location_id)
        }, 0)
      }
    }
  }, [form.values, form.initialValues, form.errors])

  const isDaySelectable = (options) => {
    return _isDaySelectable({
      ...options,
      openingHours,
      values: form.values,
      minDate,
      allowPast
    })
  }

  const isTimeSelectable = (options) => {
    return _isTimeSelectable({
      ...options,
      openingHours,
      values: form.values,
      minDate,
      allowPast
    })
  }

  const getSelectableTimeslots = (date) => {
    if (!timeslots?.size && !timeslots?.length) return []

    return OperatingRulesUtils.fixedTimeslotsForDate(timeslots, date, {
      useOpeningHours: !!openingHours,
      operatingRules: openingHours,
      allowPast
    })
  }

  const setDefaultTime = (options) => {
    return getDefaultTime({
      ...options,
      openingHours,
      allowPast
    })
  }

  return (
    <PickerContextProvider
      mode={mode}
      type={type}
      openingHours={openingHours}
      useTimes={useTimes}
      allowPast={allowPast}
      onValidate={onValidate}
      isDaySelectable={isDaySelectable}
      isTimeSelectable={isTimeSelectable}
      hasInitialValues={hasInitialValues}
      setDefaultTime={setDefaultTime}
      getSelectableTimeslots={getSelectableTimeslots}
    >
      {children}
    </PickerContextProvider>
  )
}

PickerFormComponent.propTypes = {
  children: PropTypes.node.isRequired,
  mode: PropTypes.oneOf(['freely', 'timeslot_duration', 'timeslot_fixed', 'undefined']),
  type: PropTypes.oneOf(['order', 'cart']).isRequired,
  openingHours: PropTypes.object,
  allowPast: PropTypes.bool,
  useTimes: PropTypes.bool,
  minDate: PropTypes.object,
  validate: PropTypes.func,
  onChange: PropTypes.func,
  onValidate: PropTypes.func
}

const PickerForm = withForm()(PickerFormComponent)

export const PickerProvider = ({
  children,
  mode,
  type,
  openingHours,
  ignoreDirty,
  minDate,
  initialValues = {},
  validateOnChange = true,
  validateOnBlur = false,
  enableReinitialize,
  allowPast,
  useTimes,
  onValidate,
  onChange
}) => {
  const locations = useOrm('locations', (locations) => {
    return locations.getAll({ archived_at: [null, undefined] }).sort('name')
  })

  const validateForm = (values) => {
    return validate({ values, locations, openingHours, allowPast })
  }

  // Add an empty function for handleSubmit, as it's a required prop but we
  // don't use it here. The props can still overwrite it.
  const handleSubmit = () => {}

  const initialTouched = {}

  if (initialValues) {
    const { starts_at: startsAt, stops_at: stopsAt } = initialValues

    if (startsAt) {
      initialTouched.starts_at = true
    }

    if (stopsAt) {
      initialTouched.stops_at = true
    }
  }

  return (
    <PickerForm
      mode={mode}
      type={type}
      allowPast={allowPast}
      useTimes={useTimes}
      onValidate={onValidate}
      onChange={onChange}
      minDate={minDate}
      openingHours={openingHours}
      // Formik props
      handleSubmit={handleSubmit}
      validate={validateForm}
      initialValues={initialValues}
      initialTouched={initialTouched}
      validateOnBlur={validateOnBlur}
      validateOnChange={validateOnChange}
      ignoreDirty={ignoreDirty}
      enableReinitialize={enableReinitialize}
      validateOnMount
    >
      {children}
    </PickerForm>
  )
}

PickerProvider.propTypes = {
  children: PropTypes.node,
  mode: PropTypes.oneOf(['freely', 'timeslot_duration', 'timeslot_fixed', 'undefined']),
  type: PropTypes.oneOf(['order', 'cart']).isRequired,
  openingHours: PropTypes.object,
  initialValues: PropTypes.object.isRequired,
  ignoreDirty: PropTypes.bool,
  validateOnChange: PropTypes.bool,
  validateOnBlur: PropTypes.bool,
  enableReinitialize: PropTypes.bool,
  minDate: PropTypes.object,
  allowPast: PropTypes.bool,
  useTimes: PropTypes.bool,
  onValidate: PropTypes.func,
  onChange: PropTypes.func
}
